import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import ChangeReasonDialog from "components/ChangeReasonDialog";
import EntityDetails from "components/EntityDetails";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useFormattedTitleFromUser from "hooks/useFormattedTitleFromUser";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  DeleteUserSkuRequest,
  OrganizationUserDetails,
  ResponseOfSkus,
  ResponseOfUserSkuExtended,
} from "services/KnowfullyAdminClient";

const useStyles = makeStyles((theme: Theme) => ({
  userSkuInfo: {
    mb: theme.spacing(2),
  },
}));

const DeleteUserSkuPage = () => {
  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const [deleteCofirmationOpen, deleteCofirmationOpenActions] = useBoolean(
    false
  );
  const [deleteSuccessOpen, deleteSuccessOpenActions] = useBoolean(false);
  const [changeReason, setChangeReason] = useState<string>("");

  const {
    organizationUserService,
    userSkuService,
    skuService,
    userCourseUrl,
    userDetailsUrl,
  } = useGlobalContext();

  const deleteUserSku = async () => {
    await registerPromise(
      userSkuService.deleteUserSku(
        DeleteUserSkuRequest.fromJS({
          userId: userId,
          userSkuId: userSkuId,
          organizationId: user?.organizationId,
          changeReason,
        })
      )
    );

    deleteCofirmationOpenActions.setFalse();
    queryClient.invalidateQueries(["getUserSku", userSkuId]);
    deleteSuccessOpenActions.setTrue();
  };

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;
  useFormattedTitleFromUser("deleteRegistrationTitle", user, t({ id: "user" }));

  const userSkuResult = useQuery<ResponseOfUserSkuExtended>(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { enabled: !!userResult && !userResult.isLoading }
  );

  const userSku = userSkuResult?.data?.data!;

  const skuResult = useQuery<ResponseOfSkus>(
    ["sku", userSku?.skuId],
    () => skuService.getSkuById(userSku?.skuId!),
    {
      enabled: !!userSku && !userSkuResult.isLoading,
    }
  );

  const sku = skuResult?.data?.data!;

  const loading =
    userResult.isLoading || userSkuResult.isLoading || skuResult.isLoading;

  const rows = [
    RowElement(
      "userNameLabel",
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
      loading
    ),
    RowElement("emailLabel", user?.email!, userResult.isLoading),
    RowElement("userId", user?.id!, userResult.isLoading),
    RowElement("courseName", userSku?.title ?? "", userSkuResult.isLoading),
    RowElement("skuLabel", sku?.sku ?? "", skuResult.isLoading),
    RowElement("userSkuId", userSkuId!),
    RowElement("startDateLabel", userSku?.startDate!, userSkuResult.isLoading),
    RowElement("endDateLabel", userSku?.endDate!, userSkuResult.isLoading),
    RowElement(
      "statusLabel",
      userSku?.userSkuStateName!,
      userSkuResult.isLoading
    ),
  ].filter((r) => !!r.component);

  if (userResult.isError || userSkuResult.isError || skuResult.isError) {
    return (
      <Alert severity="error">
        <pre>{t({ id: "fetchError" })}</pre>
      </Alert>
    );
  }

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userCourseUrl(userId, userSkuId)}
        labelKey="backToUserCourse"
      />

      <h1>{t({ id: "deleteRegistration" })}</h1>

      <EntityDetails
        title={t({ id: "registrationDetails" })}
        details={rows}
        sx={styles.userSkuInfo}
      />

      <Box>
        <Button
          variant="outlined"
          onClick={deleteCofirmationOpenActions.setTrue}
        >
          <T id="confirmDelete" />
        </Button>
      </Box>

      <ChangeReasonDialog
        open={deleteCofirmationOpen}
        onCancel={deleteCofirmationOpenActions.setFalse}
        handleConfirm={deleteUserSku}
        changeReason={changeReason}
        setChangeReason={setChangeReason}
      />

      <Dialog
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
        open={deleteSuccessOpen}
        onClose={() => navigate(userDetailsUrl(userId))}
      >
        <DialogTitle>
          <T id="deleteRegistrationSuccess" />
        </DialogTitle>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => navigate(userDetailsUrl(userId))}
          >
            <T id="closeButton" />
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeleteUserSkuPage;
